import { useEffect, useState } from "react";
import { useT } from "../../i18n/use-t.js";
import { identity } from "../../state/identity.js";
import { hubClient, signInToHub, signOutFromHub } from "../../state/hub.js";
import type { IdentitySession, HubProfile, HubSessionDto } from "@roqeet/core";
import { User, LogOut, Check, Globe, Edit2 } from "lucide-react";

export function IdentitySection() {
  const { t } = useT();
  const [session, setSession] = useState<IdentitySession>(identity.current());
  const [profile, setProfile] = useState<HubProfile | null>(hubClient.currentProfile());
  const [hubSession, setHubSession] = useState<HubSessionDto>({ state: "anonymous", member: null });
  const [busy, setBusy] = useState(false);
  const [editing, setEditing] = useState(false);
  const [handle, setHandle] = useState("");
  const [msg, setMsg] = useState<string | null>(null);

  useEffect(() => {
    const unsubId = identity.subscribe(setSession);
    const unsubHub = hubClient.subscribe(setProfile);
    void hubClient.getSession().then(setHubSession).catch(() => {
      setHubSession({ state: "anonymous", member: null });
    });
    return () => {
      unsubId();
      unsubHub();
    };
  }, []);

  const handleSignIn = async () => {
    if (!hubClient.isConfigured()) return;
    setBusy(true);
    setMsg(null);
    try {
      await signInToHub();
      setMsg(t("settings.identity.hubConnected") || "허브에 연결되었습니다.");
    } catch (e) {
      setMsg(t("settings.identity.hubFail", { error: e instanceof Error ? e.message : String(e) }) || "연결 실패");
    } finally {
      setBusy(false);
    }
  };

  const handleSignOut = async () => {
    setBusy(true);
    setMsg(null);
    try {
      await signOutFromHub();
      setHubSession({ state: "anonymous", member: null });
    } catch (e) {
      setMsg(t("settings.identity.hubFail", { error: e instanceof Error ? e.message : String(e) }) || "로그아웃 실패");
    } finally {
      setBusy(false);
    }
  };

  const handleSaveProfile = async () => {
    if (!handle.trim()) return;
    setBusy(true);
    setMsg(null);
    try {
      await hubClient.updateProfile({ handle: handle.trim() });
      setEditing(false);
      setMsg(t("settings.identity.profileSaved") || "프로필이 저장되었습니다.");
    } catch (e) {
      setMsg(t("settings.identity.profileFail", { error: e instanceof Error ? e.message : String(e) }) || "저장 실패");
    } finally {
      setBusy(false);
    }
  };

  const startEditing = () => {
    setHandle(profile?.handle ?? "");
    setEditing(true);
    setMsg(null);
  };

  const isGoogleConnected = session.tier === "google";
  const hubMember = hubSession.state === "member" ? hubSession.member : null;
  const hasHubIdentity = Boolean(profile || hubMember);
  const hubDisplayName = profile?.handle ?? hubMember?.handle ?? hubMember?.displayName ?? "";
  const isApiLoginAvailable =
    typeof (hubClient as { getGoogleLoginUrl?: unknown }).getGoogleLoginUrl === "function";
  const firstLetter = hubDisplayName ? hubDisplayName.charAt(0).toUpperCase() : "";

  return (
    <section className="space-y-3">
      <h2 className="text-xs font-bold uppercase tracking-wider text-ink-faint flex items-center gap-1.5">
        <User size={12} className="text-ink-faint" />
        <span>{t("settings.identity.title")}</span>
      </h2>

      <div className="rounded-2xl border border-line bg-bg-soft p-5 space-y-4 shadow-sm relative overflow-hidden">
        {/* Subtle decorative glow inside card */}
        <div className="absolute right-0 top-0 w-24 h-24 bg-accent/5 rounded-full blur-xl pointer-events-none" />

        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            {/* Visual Avatar */}
            {hasHubIdentity ? (
              <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-accent text-white font-bold text-lg shadow-md ring-2 ring-accent/20">
                {firstLetter}
              </div>
            ) : (
              <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-bg-raised border border-line text-ink-soft">
                <Globe size={20} className="stroke-[1.5]" />
              </div>
            )}

            <div className="min-w-0">
              <div className="flex items-center gap-2">
                <span className="text-sm font-semibold text-ink truncate">
                  {hasHubIdentity ? (
                    hubMember?.handle || profile?.handle ? `@${hubDisplayName}` : hubDisplayName
                  ) : isGoogleConnected ? (
                    t("settings.identity.google") || "Google 연결됨"
                  ) : (
                    t("settings.identity.anonymous") || "익명 (로컬 전용)"
                  )}
                </span>

                {/* Status Badges */}
                {hasHubIdentity ? (
                  <span className="inline-flex items-center gap-0.5 rounded-full bg-accent/15 border border-accent/20 px-2 py-0.5 text-[9px] font-semibold text-accent-soft">
                    <Check size={8} />
                    허브 연결됨
                  </span>
                ) : (
                  <span className="inline-flex items-center gap-0.5 rounded-full bg-bg-raised border border-line px-2 py-0.5 text-[9px] text-ink-soft">
                    로컬 전용
                  </span>
                )}
              </div>

              <p className="mt-1 text-[11px] text-ink-faint leading-normal">
                {hasHubIdentity
                  ? "RoQeet Hub에서 자유롭게 프로젝트를 공유하고 피드백을 주고받을 수 있습니다."
                  : t("settings.identity.note") || "구글 로그인을 통해 Hub에 리뷰를 남기거나 창작물을 공유할 수 있습니다."}
              </p>
            </div>
          </div>

          {hubClient.isConfigured() && (
            <div className="flex shrink-0 self-end sm:self-center gap-2">
              {profile ? (
                <div className="flex items-center gap-1.5">
                  <button
                    className="flex items-center gap-1 rounded-lg border border-line bg-bg/50 px-3 py-1.5 text-xs font-medium text-ink-soft hover:bg-bg-raised hover:text-ink transition"
                    onClick={startEditing}
                    disabled={busy || editing}
                  >
                    <Edit2 size={11} />
                    <span>닉네임 변경</span>
                  </button>
                  <button
                    className="flex items-center gap-1 rounded-lg border border-line bg-bg/50 px-3 py-1.5 text-xs font-medium text-red-400 hover:bg-red-500/10 hover:border-red-500/20 transition"
                    onClick={handleSignOut}
                    disabled={busy}
                  >
                    <LogOut size={11} />
                    <span>로그아웃</span>
                  </button>
                </div>
              ) : hubMember ? (
                <button
                  className="flex items-center gap-1 rounded-lg border border-line bg-bg/50 px-3 py-1.5 text-xs font-medium text-red-400 hover:bg-red-500/10 hover:border-red-500/20 transition"
                  onClick={handleSignOut}
                  disabled={busy}
                >
                  <LogOut size={11} />
                  <span>濡쒓렇?꾩썐</span>
                </button>
              ) : isGoogleConnected || isApiLoginAvailable ? (
                <button
                  className="rounded-lg bg-accent px-4 py-1.5 text-xs font-semibold text-white hover:bg-accent-soft hover:shadow-lg hover:shadow-accent/10 active:scale-95 transition"
                  onClick={handleSignIn}
                  disabled={busy}
                >
                  닉네임 설정하고 허브 시작하기
                </button>
              ) : null}
            </div>
          )}
        </div>

        {editing && profile && (
          <div className="mt-3 flex gap-2 p-3 bg-bg-raised/40 rounded-xl border border-line/60">
            <input
              type="text"
              value={handle}
              onChange={(e) => setHandle(e.target.value)}
              placeholder="새 닉네임 입력 (최대 20자)"
              className="flex-1 rounded-lg border border-line bg-bg px-3 py-1.5 text-xs text-ink outline-none focus:border-accent transition"
              maxLength={20}
              disabled={busy}
            />
            <button
              className="rounded-lg border border-line bg-bg px-3 py-1.5 text-xs text-ink-soft hover:bg-bg-raised transition"
              onClick={() => setEditing(false)}
              disabled={busy}
            >
              취소
            </button>
            <button
              className="rounded-lg bg-accent px-4 py-1.5 text-xs font-semibold text-white hover:bg-accent-soft disabled:opacity-50 transition"
              onClick={handleSaveProfile}
              disabled={busy || !handle.trim() || handle.trim() === profile.handle}
            >
              저장
            </button>
          </div>
        )}

        {msg && (
          <div className={`text-xs p-2.5 rounded-lg border flex items-center gap-1.5 ${
            msg.includes("실패")
              ? "bg-red-500/10 border-red-500/20 text-red-400"
              : "bg-bg-raised/60 border-line text-ink-soft"
          }`}>
            <span className="text-[11px] leading-relaxed">{msg}</span>
          </div>
        )}
      </div>
    </section>
  );
}
